import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { equipmentAssetsAtom, userEmailAtom } from '../../store/company';
import {
    loadFieldReportEquipment, saveFieldReportEquipmentDates,
} from '../../db/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { isBlankRow } from './sectionDefs';

// Equipment editor: a multi-select search dropdown adds one row per checked
// unit (descriptions only — no tier codes in the UI). The dropdown leads with
// two job-specific groups — owned units the Burn Tool sees charging to the
// job (one pick per unit number), and rental lines from the job's POs (with
// the PO's rental return date) — followed by the full Burn Tool asset list.
// Each row is dates only: unit number (from the units charging to the job in
// actual cost), classification, Start — the first cost the piece charged to
// the job — and the demob date behind a "this date is correct" gate.
//
// Dates come from different places by piece type. Owned iron starts at its
// first equipment charge (52100) in cost history, and has no demob date in
// JDE at all — someone on the job sets it. Rentals get both from the PO: the
// first line of the PO carries the rental window, and every line of that PO
// reports those dates. Either can be wrong, so the demob date sits behind a
// checkbox: checked means "this is correct" and locks the cell; unchecking
// unlocks it for a date pick. Corrections save to PC.Field_Report_Equipment_Dates
// keyed by job + piece, so they carry onto every later report rather than
// being re-entered daily.
// A Draft whose equipment section is still empty pulls in the pieces with
// actuals in the last forecast file (owned tiers + rental lines) so the field
// starts from what's actually burning and trims, instead of rebuilding the
// list every day.

// Owned units stop being offered once their last Burn Tool charge is older
// than this; returned rentals stay visible this long past the return date.
const OWNED_RECENT_DAYS = 45;
const RETURNED_VISIBLE_DAYS = 14;

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};
const round1 = (v) => Math.round(v * 10) / 10;
// Earliest of the ISO dates handed in, ignoring the blanks.
const earliest = (...dates) => dates.filter(Boolean).sort()[0] || null;

const CELL_BORDER = '1px solid #CBD5E1';

const thStyle = {
    border: CELL_BORDER,
    background: '#F1F5F9',
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#334155',
    textAlign: 'left',
    verticalAlign: 'bottom',
};

const tdStyle = { border: CELL_BORDER, padding: 0, verticalAlign: 'middle', background: '#fff' };

const cellInput = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    color: '#0F172A',
    padding: '7px 8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
};

// '2026-07-18' → '7/18'
const shortDate = (iso) => (iso ? `${parseInt(iso.slice(5, 7), 10)}/${parseInt(iso.slice(8, 10), 10)}` : '');

// Picker dropdown pieces shared by the three groups.
const groupHeaderStyle = {
    padding: '7px 12px 4px',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#64748B',
    background: '#F8FAFC',
    borderBottom: '1px solid #F1F5F9',
    position: 'sticky',
    top: 0,
};
const pickRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    fontSize: 13,
    color: '#0F172A',
    cursor: 'pointer',
    borderBottom: '1px solid #F1F5F9',
};
const pickBoxStyle = { width: 16, height: 16, accentColor: '#1D4ED8', flexShrink: 0 };
const pickSubStyle = { display: 'block', fontSize: 11, color: '#94A3B8', marginTop: 1 };

const EquipmentMatrix = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [equipmentAssets] = useAtom(equipmentAssetsAtom);
    const [userEmail] = useAtom(userEmailAtom);

    // Multi-select picker state.
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const pickerRef = useRef(null);
    useEffect(() => {
        const onDown = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    // Assets: value = tier code, label = "CODE - Description". The UI only
    // shows the description; rows keep storing the full label so old drafts
    // and prior-report comparisons still line up.
    const assets = useMemo(() => (equipmentAssets || []).map((a) => {
        const label = a.label || a.value || '';
        const desc = a.value && label.startsWith(`${a.value} - `) ? label.slice(String(a.value).length + 3) : label;
        return { value: a.value, label, desc };
    }), [equipmentAssets]);

    // Stored equipment string → display description.
    const descOf = useMemo(() => {
        const byLabel = Object.fromEntries(assets.map((a) => [a.label, a.desc]));
        return (stored) => byLabel[stored] || stored || '';
    }, [assets]);

    // Job-specific equipment context: owned units from Burn Tool actuals and
    // rental PO lines. Best-effort — offline or on error the picker just
    // falls back to the plain asset list.
    const [jobEquip, setJobEquip] = useState(null);
    useEffect(() => {
        let alive = true;
        setJobEquip(null);
        if (!report?.buId) return undefined;
        loadFieldReportEquipment({ buId: String(report.buId) })
            .then((res) => { if (alive && res && !res.error) setJobEquip(res); })
            .catch(() => { });
        return () => { alive = false; };
    }, [report?.buId]);

    const dayCutoff = (days) => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);

    // Owned units, one entry per unit number, keeping units still charging
    // recently or with actuals in the last forecast file (LastFileHours/Cost —
    // the delta the newest cost-history version added over the prior one).
    // assetLabel matches the Burn Tool asset list by tier code so rows line up
    // with prior-report comparisons; firstOnJob is the unit's first charge to
    // the job.
    const ownedUnits = useMemo(() => {
        const cutoff = dayCutoff(OWNED_RECENT_DAYS);
        const assetByTier = Object.fromEntries(assets.map((a) => [a.value, a]));
        const out = [];
        (jobEquip?.owned || []).forEach((u) => {
            const tier = u.Tier || '';
            const desc = u.TierDescription || tier || u.Unit_Number || '';
            const unit = u.Unit_Number ? String(u.Unit_Number) : '';
            if (!unit || (!tier && !desc)) return;
            const last = String(u.LastChargeDate || '').slice(0, 10);
            const fileHrs = num(u.LastFileHours);
            const inFile = fileHrs > 0 || num(u.LastFileCost) > 0;
            if ((!last || last < cutoff) && !inFile) return;
            const asset = assetByTier[tier];
            out.push({
                key: `U:${unit}`,
                unit,
                tier,
                desc: asset?.desc || desc,
                assetLabel: asset?.label || (tier && desc ? `${tier} - ${desc}` : desc || tier),
                firstOnJob: String(u.FirstChargeDate || '').slice(0, 10) || null,
                lastCharge: last,
                lastFileHours: round1(Math.max(fileHrs, 0)),
                inFile,
            });
        });
        return out.sort((a, b) => String(b.lastCharge).localeCompare(String(a.lastCharge))
            || a.desc.localeCompare(b.desc) || a.unit.localeCompare(b.unit));
    }, [jobEquip, assets]);

    // Rental PO lines: everything still out, plus recently returned lines
    // (tagged) so the field sees the return landed, plus anything charging in
    // the last forecast file regardless of return date. lastFileCost is the
    // PO-level actual delta, shared by every line of the PO.
    const rentalLines = useMemo(() => {
        const cutoff = dayCutoff(RETURNED_VISIBLE_DAYS);
        return (jobEquip?.rentals || []).map((l) => {
            const tierDesc = l.TierDescription && l.TierDescription !== '.' ? l.TierDescription : '';
            const label = l.PO_Line_Description || tierDesc || `PO ${l.PO_Number}`;
            return {
                key: `${l.PO_Number}:${l.PO_Line_Number}`,
                label,
                supplier: l.SupplierName || '',
                po: l.PO_Number,
                due: String(l.Rental_End_Date || '').slice(0, 10) || null,
                returned: String(l.Rental_Return_Date || '').slice(0, 10) || null,
                // The PO's first line carries the rental window for the piece;
                // where it's blank the PO's first 52200 charge stands in, and
                // when both are there the earlier one is when the piece landed.
                start: earliest(
                    String(l.PoStartDate || l.Rental_Start_Date || '').slice(0, 10) || null,
                    String(l.FirstChargeDate || '').slice(0, 10) || null,
                ),
                demob: String(l.PoDemobDate || l.Rental_End_Date || '').slice(0, 10) || null,
                lastFileCost: num(l.LastFileCost),
            };
        }).filter((l) => l.lastFileCost > 0 || !l.returned || l.returned >= cutoff);
    }, [jobEquip]);

    // PO number → the window off its first line, so rows saved on an earlier
    // day still show today's PO dates instead of a stale copy.
    const poDates = useMemo(() => {
        const map = {};
        rentalLines.forEach((l) => {
            if (l.po == null || map[l.po]) return;
            map[l.po] = { start: l.start, demob: l.demob };
        });
        return map;
    }, [rentalLines]);

    // Owned unit number → its first charge to the job.
    const unitFirst = useMemo(
        () => Object.fromEntries(ownedUnits.map((u) => [u.unit, u.firstOnJob])),
        [ownedUnits],
    );

    // Classification → the unit numbers charging to the job in cost history,
    // keyed by both the stored asset label and the plain description so rows
    // added from the full asset list (which carry no unit) still show the iron
    // actual cost says is on the job.
    const unitsByClass = useMemo(() => {
        const map = {};
        ownedUnits.forEach((u) => {
            [u.assetLabel, u.desc, u.tier].forEach((k) => {
                const key = String(k || '').trim().toLowerCase();
                if (!key) return;
                if (!map[key]) map[key] = [];
                if (!map[key].includes(u.unit)) map[key].push(u.unit);
            });
        });
        return map;
    }, [ownedUnits]);

    // Classification → the first equipment charge (52100) on the job under that
    // classification, keyed the same way as unitsByClass. A row picked off the
    // full asset list carries no unit, so this is its start date on the job.
    const classFirst = useMemo(() => {
        const map = {};
        ownedUnits.forEach((u) => {
            if (!u.firstOnJob) return;
            [u.assetLabel, u.desc, u.tier].forEach((k) => {
                const key = String(k || '').trim().toLowerCase();
                if (!key) return;
                map[key] = earliest(map[key], u.firstOnJob);
            });
        });
        return map;
    }, [ownedUnits]);

    // Saved mob/demob rows for this job, keyed 'U:<unit>' / 'P:<po>'. Local
    // edits land here immediately so the cell responds before the save
    // round-trips.
    const [savedDates, setSavedDates] = useState({});
    useEffect(() => {
        const map = {};
        (jobEquip?.dates || []).forEach((d) => {
            map[d.EquipKey] = {
                demobDate: String(d.DemobDate || '').slice(0, 10) || null,
                demobConfirmed: !!d.DemobConfirmed,
                sourceDemobDate: String(d.SourceDemobDate || '').slice(0, 10) || null,
            };
        });
        setSavedDates(map);
    }, [jobEquip]);

    // Pull the burning pieces into an empty Draft: one row per owned tier and
    // rental line with actuals in the last forecast file. Runs once per report
    // so a list the user deliberately emptied stays empty for the session.
    // Rental POs list one line per unit (25 porta-potties) plus freight/fee
    // lines, so identical descriptions collapse into one row with a quantity
    // and service-charge lines are skipped.
    const prefilledFor = useRef(null);
    useEffect(() => {
        if (readOnly || !jobEquip || !report || report.status !== 'Draft') return;
        if (prefilledFor.current === report.reportId) return;
        prefilledFor.current = report.reportId;
        if ((report.sections?.equipment || []).some((r) => !isBlankRow(r))) return;
        const ownedRows = ownedUnits
            .filter((u) => u.inFile)
            .map((u) => ({
                equipment: u.assetLabel,
                unitNumber: u.unit,
                firstOnJob: u.firstOnJob,
                quantity: '1',
            }));
        const junk = /freight|deliver|pick\s*up|set\s*up|setup|disposal|charge|credit|fee\b|tax\b|environmental|waiver|insurance|per\s*diem/i;
        const byPoDesc = {};
        rentalLines
            .filter((l) => l.lastFileCost > 0 && !l.returned && !junk.test(l.label))
            .forEach((l) => {
                const k = `${l.po}|${l.label.trim().toLowerCase()}`;
                if (!byPoDesc[k]) byPoDesc[k] = { ...l, count: 0 };
                byPoDesc[k].count += 1;
            });
        const rentalRows = Object.values(byPoDesc).map((l) => ({
            equipment: l.label,
            rented: true,
            quantity: String(l.count),
            rentalKey: l.key,
            rentalSupplier: l.supplier,
            rentalPo: l.po,
            rentalDue: l.due,
            rentalReturn: l.returned,
            firstOnJob: l.start,
        }));
        if (!ownedRows.length && !rentalRows.length) return;
        setReport({ ...report, sections: { ...report.sections, equipment: [...ownedRows, ...rentalRows] } });
        setDirty(true);
    }, [readOnly, jobEquip, report, ownedUnits, rentalLines, setReport, setDirty]);

    if (!report) return null;
    const rows = report.sections?.equipment || [];

    const setRows = (next) => {
        setReport({ ...report, sections: { ...report.sections, equipment: next } });
        setDirty(true);
    };

    const updateRow = (i, patch) => {
        const next = rows.slice();
        next[i] = { ...next[i], ...patch };
        setRows(next);
    };
    const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    const selected = new Set(rows.map((r) => r.equipment).filter(Boolean));
    const rentalSelected = new Set(rows.map((r) => r.rentalKey).filter(Boolean));
    const unitSelected = new Set(rows.map((r) => r.unitNumber).filter(Boolean));

    // Checking adds a row for the unit; unchecking takes it back off the list.
    const togglePick = (asset) => {
        if (readOnly) return;
        if (selected.has(asset.label)) {
            setRows(rows.filter((r) => r.equipment !== asset.label));
        } else {
            setRows([...rows.filter((r) => !isBlankRow(r)), { equipment: asset.label }]);
        }
    };

    // Owned Burn Tool unit: one row per unit number, carrying its first date
    // on the job so the row still reads right after the unit stops charging.
    const toggleOwned = (u) => {
        if (readOnly) return;
        if (unitSelected.has(u.unit)) {
            setRows(rows.filter((r) => r.unitNumber !== u.unit));
        } else {
            setRows([...rows.filter((r) => !isBlankRow(r)), {
                equipment: u.assetLabel,
                unitNumber: u.unit,
                firstOnJob: u.firstOnJob,
                quantity: '1',
            }]);
        }
    };

    // Rental PO line: the row carries the PO context (supplier, PO number,
    // due-back / returned dates) and comes in with Rented already checked.
    const toggleRental = (l) => {
        if (readOnly) return;
        if (rentalSelected.has(l.key)) {
            setRows(rows.filter((r) => r.rentalKey !== l.key));
        } else {
            setRows([...rows.filter((r) => !isBlankRow(r)), {
                equipment: l.label,
                rented: true,
                rentalKey: l.key,
                rentalSupplier: l.supplier,
                rentalPo: l.po,
                rentalDue: l.due,
                rentalReturn: l.returned,
                firstOnJob: l.start,
            }]);
        }
    };

    const searchLc = search.trim().toLowerCase();
    const filteredAssets = assets.filter((a) => !searchLc
        || a.desc.toLowerCase().includes(searchLc) || String(a.value || '').toLowerCase().includes(searchLc));
    const filteredOwned = ownedUnits.filter((u) => !searchLc
        || u.desc.toLowerCase().includes(searchLc) || String(u.tier).toLowerCase().includes(searchLc)
        || u.unit.toLowerCase().includes(searchLc));
    const filteredRentals = rentalLines.filter((l) => !searchLc
        || l.label.toLowerCase().includes(searchLc) || l.supplier.toLowerCase().includes(searchLc)
        || String(l.po).toLowerCase().includes(searchLc));

    const realRows = rows.filter((r) => !isBlankRow(r));

    const colCount = 5 + (readOnly ? 0 : 1);
    const placeholderCount = readOnly ? 0 : Math.max(0, 5 - realRows.length);

    const dateCell = (val) => (
        <td style={tdStyle}>
            <div style={{ ...cellInput, color: val ? '#0F172A' : '#94A3B8' }}>
                {val ? shortDate(val) : '—'}
            </div>
        </td>
    );

    // Job-level key for the mob/demob row: owned iron by unit number, rentals
    // by PO. A hand-picked asset row has neither, so its dates stay on the
    // report row only.
    const equipKeyOf = (r) => (r.unitNumber ? `U:${r.unitNumber}`
        : (r.rentalPo != null ? `P:${r.rentalPo}` : null));

    // What the systems say, before any correction: rentals get the PO's first
    // line, owned iron has no demob date in JDE at all.
    const systemDemobOf = (r) => (r.unitNumber ? null
        : (poDates[r.rentalPo]?.demob || r.rentalDue || null));
    // Start on the job = the first cost that hit the job for the piece: the
    // unit's first 52100 charge for owned iron, the PO's rental start or first
    // 52200 charge for rentals. A row picked off the asset list has neither, so
    // it falls back to the first charge under its classification.
    const startOf = (r) => {
        if (r.unitNumber) return unitFirst[r.unitNumber] || r.firstOnJob || null;
        if (r.rentalPo != null) return poDates[r.rentalPo]?.start || r.firstOnJob || null;
        return r.firstOnJob || classFirst[String(r.equipment || '').trim().toLowerCase()] || null;
    };

    // Saved correction wins over the system date; the confirm flag is the gate.
    const demobStateOf = (r) => {
        const key = equipKeyOf(r);
        const saved = key ? savedDates[key] : null;
        const system = systemDemobOf(r);
        return {
            key,
            system,
            date: (saved?.demobDate ?? r.demobDate) || system || '',
            confirmed: saved ? saved.demobConfirmed : !!r.demobConfirmed,
        };
    };

    // Write the correction to the job (so tomorrow's report opens with it) and
    // mirror it onto the row so the filed report keeps what it showed.
    const setDemob = (i, r, patch) => {
        if (readOnly) return;
        const state = demobStateOf(r);
        const next = {
            demobDate: patch.demobDate !== undefined ? patch.demobDate : state.date,
            demobConfirmed: patch.demobConfirmed !== undefined ? patch.demobConfirmed : state.confirmed,
        };
        updateRow(i, next);
        if (!state.key) return;
        setSavedDates((prevMap) => ({
            ...prevMap,
            [state.key]: { ...next, sourceDemobDate: state.system },
        }));
        saveFieldReportEquipmentDates({
            buId: String(report.buId),
            equipKey: state.key,
            kind: r.unitNumber ? 'owned' : 'rental',
            unitNumber: r.unitNumber || null,
            poNumber: r.unitNumber ? null : r.rentalPo,
            classification: descOf(r.equipment) || null,
            startDate: startOf(r),
            demobDate: next.demobDate || null,
            demobConfirmed: next.demobConfirmed,
            sourceDemobDate: state.system,
            updatedBy: userEmail || report.preparedBy || null,
        }).catch(() => { /* offline: the row still carries it into the report */ });
    };

    // Unit numbers for the row: the unit the row was picked with, else whatever
    // is charging to the job in cost history under that classification (a tier
    // with five excavators on the job lists all five). Rentals have no unit
    // number in JDE, so those stay on the PO.
    const unitsOf = (r) => {
        if (r.unitNumber) return [r.unitNumber];
        if (r.rentalPo != null) return [];
        return unitsByClass[String(r.equipment || '').trim().toLowerCase()] || [];
    };

    return (
        <View>
            {/* Multi-select add: search, then check every unit on site today. */}
            {!readOnly && (
                <div ref={pickerRef} style={{ position: 'relative', maxWidth: 420, marginBottom: 8 }}>
                    <input
                        type="text"
                        value={search}
                        placeholder="Search equipment — check all that apply…"
                        onFocus={() => setOpen(true)}
                        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
                        style={{ width: '100%', fontSize: 13, padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, boxSizing: 'border-box', outline: 'none' }}
                    />
                    {open && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #CBD5E1', borderRadius: 8, marginTop: 2, maxHeight: 320, overflowY: 'auto', boxShadow: '0 8px 20px rgba(15,23,42,0.12)' }}>
                            {filteredAssets.length === 0 && filteredOwned.length === 0 && filteredRentals.length === 0 && (
                                <div style={{ padding: '10px 12px', fontSize: 12, color: '#94A3B8' }}>No equipment matches the search.</div>
                            )}
                            {filteredOwned.length > 0 && (
                                <div style={groupHeaderStyle}>On the Job — Burn Tool</div>
                            )}
                            {filteredOwned.map((u) => (
                                <label
                                    key={`owned-${u.key}`}
                                    style={{ ...pickRowStyle, background: unitSelected.has(u.unit) ? '#EFF6FF' : '#fff' }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={unitSelected.has(u.unit)}
                                        onChange={() => toggleOwned(u)}
                                        style={pickBoxStyle}
                                    />
                                    <span>
                                        {`${u.unit} — ${u.desc}`}
                                        <span style={pickSubStyle}>
                                            {[
                                                u.firstOnJob ? `on job since ${shortDate(u.firstOnJob)}` : null,
                                                `last charged ${shortDate(u.lastCharge)}`,
                                                u.lastFileHours > 0 ? `${u.lastFileHours} hrs in last forecast`
                                                    : (u.inFile ? 'charging in last forecast' : null),
                                            ].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                </label>
                            ))}
                            {filteredRentals.length > 0 && (
                                <div style={groupHeaderStyle}>Rentals — Job POs</div>
                            )}
                            {filteredRentals.map((l) => (
                                <label
                                    key={`rental-${l.key}`}
                                    style={{ ...pickRowStyle, background: rentalSelected.has(l.key) ? '#EFF6FF' : '#fff' }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={rentalSelected.has(l.key)}
                                        onChange={() => toggleRental(l)}
                                        style={pickBoxStyle}
                                    />
                                    <span>
                                        {l.label}
                                        <span style={pickSubStyle}>
                                            {[l.supplier, `PO ${l.po}`,
                                                l.start ? `on job ${shortDate(l.start)}` : null,
                                                l.returned ? `returned ${shortDate(l.returned)}`
                                                    : (l.demob ? `demob ${shortDate(l.demob)}` : null),
                                                l.lastFileCost > 0 ? 'charging in last forecast' : null,
                                            ].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                </label>
                            ))}
                            {filteredAssets.length > 0 && (filteredOwned.length > 0 || filteredRentals.length > 0) && (
                                <div style={groupHeaderStyle}>All Equipment</div>
                            )}
                            {filteredAssets.map((a) => (
                                <label
                                    key={a.label}
                                    style={{ ...pickRowStyle, background: selected.has(a.label) ? '#EFF6FF' : '#fff' }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.has(a.label)}
                                        onChange={() => togglePick(a)}
                                        style={pickBoxStyle}
                                    />
                                    {a.desc}
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: 100, minWidth: 80 }}>Unit #</th>
                            <th style={{ ...thStyle, width: 260, minWidth: 170 }}>Classification</th>
                            <th style={{ ...thStyle, width: 110 }} title="First cost charged to the job for this piece">Start</th>
                            <th style={{ ...thStyle, width: 130 }}>Demob Date</th>
                            <th style={{ ...thStyle, width: 70, textAlign: 'center' }}>Date OK</th>
                            {!readOnly && <th style={{ ...thStyle, minWidth: 30, width: 30 }} />}
                        </tr>
                    </thead>
                    <tbody>
                        {readOnly && realRows.length === 0 && (
                            <tr>
                                <td colSpan={colCount} style={{ ...tdStyle, background: '#F8FAFC' }}>
                                    <div style={{ ...cellInput, color: '#94A3B8', fontStyle: 'italic' }}>
                                        No equipment entries.
                                    </div>
                                </td>
                            </tr>
                        )}
                        {rows.map((r, i) => (isBlankRow(r) ? null : (
                            <tr key={i}>
                                {(() => {
                                    const units = unitsOf(r);
                                    return (
                                        <td style={tdStyle}>
                                            <div style={{ ...cellInput, color: units.length ? '#0F172A' : '#94A3B8', fontSize: units.length > 1 ? 12 : 13 }}>
                                                {units.length ? units.join(', ')
                                                    : (r.rentalPo != null ? `PO ${r.rentalPo}` : '—')}
                                            </div>
                                        </td>
                                    );
                                })()}
                                <td style={tdStyle}>
                                    <div style={{ ...cellInput }}>
                                        {descOf(r.equipment)}
                                        {r.rentalPo != null && (
                                            <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                                                {[r.rentalSupplier, `PO ${r.rentalPo}`,
                                                    r.rentalReturn ? `Returned ${shortDate(r.rentalReturn)}` : null,
                                                ].filter(Boolean).join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                {dateCell(startOf(r))}
                                {(() => {
                                    const d = demobStateOf(r);
                                    const locked = readOnly || d.confirmed;
                                    return (
                                        <>
                                            <td style={{ ...tdStyle, background: locked ? '#F8FAFC' : '#fff' }}>
                                                {locked ? (
                                                    <div style={{ ...cellInput, color: d.date ? '#0F172A' : '#94A3B8' }}>
                                                        {d.date ? shortDate(d.date) : (readOnly ? '—' : 'not set')}
                                                    </div>
                                                ) : (
                                                    <input
                                                        type="date"
                                                        value={d.date || ''}
                                                        onChange={(e) => setDemob(i, r, { demobDate: e.target.value })}
                                                        style={{ ...cellInput, colorScheme: 'light' }}
                                                    />
                                                )}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={d.confirmed}
                                                    disabled={readOnly}
                                                    title={d.confirmed
                                                        ? 'Confirmed correct — uncheck to change the demob date'
                                                        : 'Check when this demob date is correct'}
                                                    onChange={(e) => setDemob(i, r, { demobConfirmed: e.target.checked })}
                                                    style={{ width: 16, height: 16, accentColor: '#1D4ED8', verticalAlign: 'middle', cursor: readOnly ? 'default' : 'pointer' }}
                                                />
                                            </td>
                                        </>
                                    );
                                })()}
                                {!readOnly && (
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <button
                                            onClick={() => delRow(i)}
                                            title="Remove row"
                                            style={{ border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '6px 8px' }}
                                        >×</button>
                                    </td>
                                )}
                            </tr>
                        )))}
                        {Array.from({ length: placeholderCount }).map((_, i) => (
                            <tr key={`placeholder-${i}`}>
                                <td style={tdStyle} />
                                <td style={tdStyle}>
                                    <div style={{ ...cellInput, color: '#CBD5E1', fontStyle: 'italic' }}>
                                        {i === 0 && realRows.length === 0 ? 'Search above and check the units on site…' : ' '}
                                    </div>
                                </td>
                                <td style={tdStyle} />
                                <td style={tdStyle} />
                                <td style={tdStyle} />
                                {!readOnly && <td style={tdStyle} />}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </View>
    );
};

export default EquipmentMatrix;
